import { useEffect, useState } from 'react';
import { ACCESS_LEVELS } from '../../../../constants/value';
import { getValueList } from '../../../../sources/value';
import translations from '../../../../translations';
import type { FormalValueReference, SubflowArgument } from '../../../../types/graph';
import type { ValueElementIdAPI, ValueElementIdReferenceAPI } from '../../../../types/value';
import ButtonDefault from '../../../buttons/ButtonDefault';
import Loader from '../../../loader/Loader';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';
import InputOutputValues from './InputOutputValues';
import { matchAllSubFlowArguments } from './subflowUtils';

/**
 * @param {String} flowId
 * @param {String} subFlowId
 * @param {Array} subFlowArguments The raw arguments metadata for the subflow map element
 * @param {Array} subFlowArgumentsToBeRendered The subflow arguments formatted as such to tell
 * the UI how to render the argument e.g how to display the value selector etc
 *
 * @param {Function} setSubFlowArguments Callback for setting arguments in the root components state
 * @param {Function} onSelect Callback for updating a single argument
 * @param {Function} togglePicker Callback to trigger opening/closing the value picker modal
 * @param {Boolean} isLoading Determines whether to show a loading indicator
 * @param {Boolean} isMatching Determines whether to set the matching checkbox to checked/unchecked
 * @param {Function} onMatch Callback for setting subflow arguments in root component state
 * after arguments have been matched
 *
 * @description Renders a UI for displaying subflow arguments
 * across all value access type (input, output, input_output).
 * Displays a checkbox to match/unmatch vales to be passed
 * in/out of the subflow.
 */

type Props = {
    subFlowId: string;
    subFlowArguments: SubflowArgument[];
    subFlowArgumentsToBeRendered: FormalValueReference[];
    setSubFlowArguments: (subFlowArguments: FormalValueReference[], type: string) => void;
    onSelect: (value: ValueElementIdAPI | null, id: string) => void;
    isLoading: boolean;
    onMatch: (matches: FormalValueReference[], matchCount: number) => void;
};

const SubFlowArguments = ({
    subFlowId,
    subFlowArguments,
    subFlowArgumentsToBeRendered,
    setSubFlowArguments,
    onSelect,
    onMatch,
    isLoading,
}: Props) => {
    /**
     * We need to set all value references available inside the tenant
     * into state as they are used for mapping the "value to be passed"
     * part of the subflow argument when we need to match arguments
     */
    const [valueReferences, setValueReferences] = useState<ValueElementIdReferenceAPI[]>([]);

    const [isFetchingValues, setIsFetchingValues] = useState(true);

    const dispatchNotification = useNotificationDispatch();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        fetchValues();
    }, []);

    const fetchValues = async () => {
        try {
            const valueRefs = await getValueList(null);

            setValueReferences(valueRefs);
            setIsFetchingValues(false);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }
    };

    const childProps = {
        subFlowId,
        subFlowArguments,
        setSubFlowArguments,
        onSelect,
        isLoading,
        subFlowArgumentsToBeRendered,
    };

    return (
        <>
            {isFetchingValues ? (
                <Loader />
            ) : (
                <>
                    <div>
                        <ButtonDefault
                            className="match-args"
                            onClick={() =>
                                matchAllSubFlowArguments(
                                    valueReferences,
                                    onMatch,
                                    subFlowArgumentsToBeRendered,
                                )
                            }
                        >
                            {translations.SUBFLOW_auto_match_arguments_button_label}
                        </ButtonDefault>
                    </div>
                    <InputOutputValues
                        valueReferences={valueReferences}
                        access={ACCESS_LEVELS['INPUT'].value}
                        {...childProps}
                    />

                    <InputOutputValues
                        valueReferences={valueReferences}
                        access={ACCESS_LEVELS['OUTPUT'].value}
                        {...childProps}
                    />

                    <InputOutputValues
                        valueReferences={valueReferences}
                        access={ACCESS_LEVELS['INPUT_OUTPUT'].value}
                        {...childProps}
                    />

                    <InputOutputValues
                        valueReferences={valueReferences}
                        access={ACCESS_LEVELS['PUBLIC'].value}
                        {...childProps}
                    />
                </>
            )}
        </>
    );
};

export default SubFlowArguments;
